import React, {useEffect, useState} from 'react';
import {debug} from './common';
import {analytics, auth, User} from './firebase';
import {UserDocType} from './UserDocType';
import buildNumbers from './build-numbers.json';
import SignInUI from './SignInUI';
//import {User} from 'firebase/auth';
import {setUserId, setUserProperties} from 'firebase/analytics';
type Props = {
  children: React.ReactNode;
};

type ContextType = {
  user: User | null;
  userDoc: UserDocType | null;
  authenticated: boolean;
  hasEmail: boolean;
  userEmail: string | null;
  nativeAppVer: string | null;

  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  setUserDoc: React.Dispatch<React.SetStateAction<UserDocType | null>>;
  showSignIn: Function;
};

const UserContext = React.createContext<ContextType | null>(null);
export const UserContextConsumer = UserContext.Consumer;

export const useUserContext = () => {
  const nullable = React.useContext(UserContext);
  if (nullable) {
    return nullable as ContextType;
  }
  throw new Error('Context is not yet initialized.');
};

let isFirstRealLoad = true;
export const usePrefs = () => {
  try {
    const prefs = useUserContext().userDoc?.prefs;
    if (prefs && Object.keys(prefs).length > 0) {
      if (isFirstRealLoad) {
        isFirstRealLoad = false;
        // Sync local cache with user prefs that may have been updated elsewhere.
        localStorage.setItem('prefs', JSON.stringify(prefs));
      }
      return prefs;
    }
    const stored = localStorage.getItem('prefs');
    if (stored) {
      console.log(`Using cached prefs: ${stored}`);
      return JSON.parse(stored);
    }
  } catch (e: any) {
    console.error(e);
  }
  return {};
};

export const UserProvider: React.FC<Props> = ({children}: Props) => {
  const [userDoc, setUserDoc] = useState<UserDocType | null>(null);
  const [user, setUser] = useState<User | null>(auth.currentUser);
  const [authenticated, setAuthenticated] = useState(
    !!user || localStorage.getItem('authenticated') === 'true'
  );
  const [hasEmail, setHasEmail] = useState(
    !!auth.currentUser?.email || localStorage.getItem('hasEmail') === 'true'
  );
  const [userEmail, setUserEmail] = useState<string | null>(
    auth.currentUser?.email || localStorage.getItem('userEmail') || null
  );
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user: User | null) => {
      debug(`authenticated:${!!user} hasEmail:${!!user?.email}`);
      localStorage.setItem('authenticated', user ? 'true' : 'false');
      localStorage.setItem('hasEmail', user?.email ? 'true' : 'false');
      localStorage.setItem('userEmail', user?.email ?? '');
      setUser(user);
      setAuthenticated(!!user);
      setHasEmail(!!user?.email);
      setUserEmail(user?.email ?? null);
      if (user?.uid) {
        setUserId(analytics, user!.uid);
        console.log(`UID: ${user!.uid}`);
      }
      if (user) {
        debug(`email:${user.email}`);
      }
    });
    return () => {
      unsubscribe();
    };
  }, []);

  const windowNav: any = window.navigator;
  const isStandalone =
    window.matchMedia('(display-mode:standalone)').matches ||
    windowNav.standalone;

  useEffect(() => {
    const getInstalledRelatedApps = windowNav.getInstalledRelatedApps;
    if (getInstalledRelatedApps) {
      type NativeApp = {id: string; version: string};
      getInstalledRelatedApps
        .call(navigator)
        .then((installedApps: NativeApp[]) => {
          try {
            const nativeApp = installedApps.find(
              (app: NativeApp) => app.id === 'com.drinkagenda.twa'
            );
            if (nativeApp) {
              setNativeAppVer(nativeApp.version);
            }
          } catch (e: any) {
            console.error(e);
          }
        })
        .catch((e: any) => console.error(e));
    }
  }, [windowNav.getInstalledRelatedApps]);

  const [nativeAppVer, setNativeAppVer] = useState<string | null>(null);
  useEffect(() => {
    const user_props = {
      site_version: buildNumbers.shortVersion,
      ...(nativeAppVer === null ? {} : {app_ver: nativeAppVer}),
      ...(userEmail === null ? {} : {user_email: userEmail}),
      ...(isStandalone === null ? {} : {is_standalone: isStandalone}),
    };
    debug(user_props);
    setUserProperties(analytics, user_props);
  }, [userEmail, isStandalone, nativeAppVer]);

  const [showSigninCount, setShowSigninCount] = React.useState(0);
  function showSignIn() {
    setShowSigninCount(showSigninCount + 1);
  }

  return (
    <UserContext.Provider
      value={{
        user,
        userDoc,
        authenticated,
        hasEmail,
        userEmail,
        setUser,
        setUserDoc,
        showSignIn,
        nativeAppVer,
      }}
    >
      {children}
      <SignInUI showRequestCount={showSigninCount} />
    </UserContext.Provider>
  );
};
